import React, { useEffect, useMemo, useState } from "react";
import Cropper from "react-easy-crop";
import { X } from "lucide-react";
import { getCroppedBlob } from "./cropUtils";

function prettyBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
}

export default function ImageEditModal({
  title = "Chỉnh ảnh trước khi upload",
  file,
  shape = "rect", // rect | round
  onCancel,
  onConfirm, // async (processedFile) => void
}) {
  const [imgUrl, setImgUrl] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lockCenter, setLockCenter] = useState(shape === "round");

  const inputName = file?.name || "image.png";
  const inputInfo = useMemo(() => {
    if (!file) return "";
    return `${file.type || "unknown"} · ${prettyBytes(file.size)}`;
  }, [file]);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setLockCenter(shape === "round");
    return () => URL.revokeObjectURL(url);
  }, [file, shape]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const doConfirm = async () => {
    if (!imgUrl || !croppedAreaPixels) return;
    setBusy(true);
    try {
      const outBlob = await getCroppedBlob(imgUrl, croppedAreaPixels, rotation, "image/png", {
        mask: shape === "round" ? "circle" : "none",
        // avatar: resize để giảm dung lượng (vẫn đủ nét)
        maxSize: shape === "round" ? 512 : 1400,
      });
      const outFile = new File([outBlob], inputName.replace(/\.[^.]+$/, "") + ".png", {
        type: "image/png",
      });
      await onConfirm?.(outFile);
    } finally {
      setBusy(false);
    }
  };

  const setRotationSafe = (deg) => {
    const n = Number(deg);
    if (Number.isNaN(n)) return;
    const normalized = ((n % 360) + 360) % 360;
    setRotation(normalized);
  };

  const resetAll = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-6"
      onClick={onCancel}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-admin-border bg-admin-surface shadow-soft-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-admin-border bg-admin-surface px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <p className="text-sm font-extrabold text-admin-text">{title}</p>
            <p className="truncate text-[11px] text-admin-textMuted">
              {inputName} {inputInfo ? `· ${inputInfo}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-admin-border bg-admin-surface text-admin-textMuted shadow-sm transition hover:bg-admin-muted hover:text-admin-text"
            aria-label="Close"
          >
            <X size={17} />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 gap-4 bg-admin-muted/30 p-4 sm:grid-cols-[1fr,280px] sm:p-5">
          <div className="relative overflow-hidden rounded-2xl border border-admin-border bg-black shadow-soft">
            {imgUrl && (
              <Cropper
                image={imgUrl}
                crop={lockCenter ? { x: 0, y: 0 } : crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                cropShape={shape === "round" ? "round" : "rect"}
                showGrid={shape !== "round"}
                onCropChange={(c) => setCrop(lockCenter ? { x: 0, y: 0 } : c)}
                onRotationChange={setRotation}
                onZoomChange={setZoom}
                onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
              />
            )}

            {/* Center guide (helps align avatar) */}
            {shape === "round" && (
              <>
                <div className="pointer-events-none absolute left-1/2 top-1/2 h-8 w-[1px] -translate-x-1/2 -translate-y-1/2 bg-white/60" />
                <div className="pointer-events-none absolute left-1/2 top-1/2 h-[1px] w-8 -translate-x-1/2 -translate-y-1/2 bg-white/60" />
              </>
            )}
          </div>

          <div className="space-y-3">
            <div className="rounded-2xl border border-admin-border bg-admin-surface p-3 shadow-sm">
              <p className="mb-2 text-xs font-extrabold uppercase tracking-wider text-admin-textMuted">
                Công cụ
              </p>
              <div className="space-y-3">
                {shape === "round" && (
                  <label className="flex items-center justify-between gap-3 rounded-xl border border-admin-border bg-admin-surface px-3 py-2 text-xs font-semibold text-admin-text">
                    <span>
                      Căn giữa (khóa tâm)
                      <span className="ml-1 text-[11px] font-normal text-admin-textMuted">— dễ canh avatar tròn</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={lockCenter}
                      disabled={busy}
                      onChange={(e) => setLockCenter(e.target.checked)}
                    />
                  </label>
                )}

                <div>
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-admin-textMuted">Xoay</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={0}
                        max={360}
                        step={1}
                        value={Math.round(rotation)}
                        disabled={busy}
                        onChange={(e) => setRotationSafe(e.target.value)}
                        className="w-20 rounded-lg border border-admin-border bg-admin-surface px-2 py-1 text-xs font-semibold text-admin-text outline-none focus:border-admin-primary focus:ring-2 focus:ring-[rgba(37,106,191,0.14)]"
                      />
                      <span className="text-[11px] text-admin-textMuted">°</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={360}
                    step={1}
                    value={rotation}
                    disabled={busy}
                    onChange={(e) => setRotationSafe(e.target.value)}
                    className="w-full"
                  />
                  <div className="mt-1 text-[11px] text-admin-textMuted">\( {Math.round(rotation)}° \)</div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-admin-border bg-admin-surface p-3 shadow-sm">
              <p className="mb-2 text-xs font-extrabold uppercase tracking-wider text-admin-textMuted">Zoom</p>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                disabled={busy}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full"
              />
              <div className="mt-1 text-[11px] text-admin-textMuted">\( {zoom.toFixed(2)}x \)</div>
            </div>

            <button
              type="button"
              onClick={resetAll}
              disabled={busy}
              className="w-full rounded-xl border border-admin-border bg-admin-surface px-4 py-2.5 text-sm font-extrabold text-admin-text shadow-sm transition hover:bg-admin-muted disabled:opacity-50"
            >
              Reset
            </button>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                className="inline-flex flex-1 items-center justify-center rounded-xl border border-admin-border bg-admin-surface px-4 py-2.5 text-sm font-extrabold text-admin-text transition hover:bg-admin-muted disabled:opacity-50"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={doConfirm}
                disabled={busy || !croppedAreaPixels}
                className="inline-flex flex-1 items-center justify-center rounded-xl bg-admin-primary px-4 py-2.5 text-sm font-extrabold text-white shadow-sm transition hover:bg-admin-primaryHover disabled:opacity-50"
              >
                {busy ? "Đang xử lý…" : "Dùng ảnh này"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

